import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { throwError } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { Globals } from '@core/globals';
import { ButtonComponent } from '@atoms/button/button.component';
import { CheckboxComponent } from '@atoms/checkbox/checkbox.component';
import { FormComponent } from '@atoms/form/form.component';
import { FormInputsComponent } from '@atoms/form/form-inputs/form-inputs.component';
import { FormSectionComponent } from '@atoms/form/form-section/form-section.component';
import { FormTitleComponent } from '@atoms/form/form-title/form-title.component';
import { GridComponent } from '@atoms/grid/grid.component';
import { InputComponent } from '@atoms/input/input.component';
import { LinkComponent } from '@atoms/link/link.component';
import { ParagraphComponent } from '@atoms/paragraph/paragraph.component';
import { PhoneInputComponent } from '@atoms/phone-input/phone-input.component';
import { SelectComponent } from '@atoms/select/select.component';
import { SelectItemComponent } from '@atoms/select/select-item/select-item.component';
import { TextareaComponent } from '@atoms/textarea/textarea.component';
import { CarInfoCardComponent } from '@molecules/car-info-card/car-info-card.component';
import { ContactInformationsComponent } from '@molecules/contact-informations/contact-informations.component';
import { FormThanksMessageComponent } from '@molecules/form-thanks-message/form-thanks-message.component';
import { ModalHeaderComponent } from '@molecules/modal/modal-header/modal-header.component';
import { ModalMainComponent } from '@molecules/modal/modal-main/modal-main.component';
import { ModalMainContentComponent } from '@molecules/modal/modal-main-content/modal-main-content.component';
import { ModalSidebarComponent } from '@molecules/modal/modal-sidebar/modal-sidebar.component';
import { ModalTitleComponent } from '@molecules/modal/modal-title/modal-title.component';
import { MakeModelSingleComponent } from '@organisms/make-model-single/make-model-single.component';
import { CarInfoService } from '@services/car-info.service';
import { FilterDataService } from '@services/filter-data.service';
import { GlobalsService } from '@services/globals.service';
import { ReservationService } from '@services/reservation.service';
import { CarItem } from '@models/car.model';
import { GtmService } from '@services/gtm.service';
import { FilterDataMileage } from '@models/filter-data.model';

@Component({
  selector: 'app-reservation-modal',
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    ButtonComponent,
    CheckboxComponent,
    FormComponent,
    FormInputsComponent,
    FormThanksMessageComponent,
    FormSectionComponent,
    FormTitleComponent,
    GridComponent,
    InputComponent,
    LinkComponent,
    ParagraphComponent,
    PhoneInputComponent,
    SelectComponent,
    SelectItemComponent,
    TextareaComponent,
    CarInfoCardComponent,
    ContactInformationsComponent,
    ModalHeaderComponent,
    ModalMainComponent,
    ModalMainContentComponent,
    ModalSidebarComponent,
    ModalTitleComponent,
    MakeModelSingleComponent,
  ],
  providers: [DatePipe],
  templateUrl: './reservation-modal.component.html',
  styleUrl: './reservation-modal.component.scss',
})
export class ReservationModalComponent implements OnInit {
  private fb = inject(FormBuilder);
  private datePipe = inject(DatePipe);
  private destroyRef = inject(DestroyRef);
  public globals = inject(Globals);
  private carInfoService = inject(CarInfoService);
  public filterData = inject(FilterDataService);
  private globalsService = inject(GlobalsService);
  private reservationService = inject(ReservationService);
  private translateService = inject(TranslateService);
  private gtmService = inject(GtmService);
  @Input() data?: { car: CarItem; type: 'reservation' | 'carInfo' };
  public form = this.fb.nonNullable.group({
    name: ['', { nonNullable: true, validators: [Validators.required] }],
    email: ['', { nonNullable: true, validators: [Validators.required, Validators.email] }],
    phone: ['', { nonNullable: true, validators: [Validators.required] }],
    phoneCode: ['+420', [Validators.required]],
    message: ['', { nonNullable: true, validators: [] }],
    // date: ['', { nonNullable: true, validators: [Validators.required] }],
    // time: ['', { nonNullable: true, validators: [Validators.required] }],
    sellCar: [false, { nonNullable: true, validators: [] }],
    sellCarMakeModel: [undefined as any, { nonNullable: true, validators: [] }],
    sellCarMileage: ['', { nonNullable: true, validators: [] }],
    sellCarYear: ['', { nonNullable: true, validators: [] }],
    sellCarFuel: ['', { nonNullable: true, validators: [] }],
    sellCarPrice: ['', { nonNullable: true, validators: [] }],
    isCompany: [false, { nonNullable: true, validators: [] }],
    marketingAgree: [false, { nonNullable: true, validators: [] }],
  });
  public timeOptions: string[] = [];
  public step = 1;
  private userIp: string = '';
  public mergedMileageOptions: FilterDataMileage[] = [];

  private updateMergedMileageOptions() {
    const apiOptions = this.filterData.filterData()?.mileageTo || [];
    const customOptions = [
      { id: 180000, slug: 180000, title: this.translateService.instant('filter.mileage.to', { mileage: '180 000 km' }) },
      { id: 200000, slug: 200000, title: this.translateService.instant('filter.mileage.to', { mileage: '200 000 km' }) },
      { id: 250000, slug: 250000, title: this.translateService.instant('filter.mileage.to', { mileage: '250 000 km' }) },
      { id: 250001, slug: 250001, title: this.translateService.instant('filter.mileage.above', { mileage: '250 000 km' }) },
    ];
    const ids = new Set(apiOptions.map((o: any) => o.id));
    const merged = [...apiOptions];
    for (const opt of customOptions) {
      if (!ids.has(opt.id)) merged.push(opt);
    }
    this.mergedMileageOptions = merged;
  }

  send(): void {
    if (this.form.invalid) return;
    const formData = this.form.getRawValue();
    let message = formData.message + `, Firma: ${formData.isCompany ? 'Ano' : 'Ne'}, mám zájem i o prodej svého vozu: ${formData.sellCar ? 'Ano' : 'Ne'}`;
    if (formData.sellCar) message += `,  můj vůz - najeto: ${formData.sellCarMileage === '250001' ? this.translateService.instant('filter.mileage.to', { mileage: '250 000 km' }) : formData.sellCarMileage}, rok: ${formData.sellCarYear}, palivo: ${formData.sellCarFuel}, cena: ${formData.sellCarPrice}, značka: ${formData.sellCarMakeModel.make}, model: ${formData.sellCarMakeModel.model}`;
    const phoneCode = this.globalsService.searchPhoneCountryCode(formData.phoneCode.replace('+', ''));

    if (this.data?.type === 'reservation') {
      const reservationRequest = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        phoneCode: (phoneCode ?? 44).toString(),
        message: message,
        date: new Date(),
        marketingAgree: formData.marketingAgree,
      };
      this.reservationService
        .sendForm(this.data!.car.aaaid, reservationRequest)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.form.reset();
            this.form.markAsUntouched();
            this.step = 2;

            const dataLayer = {
              event: 'page_view',
              pageURL: '/' + this.globals.language() + '/formular-prijat',
              pageType: 'Purchase-2',
              pageTitle: 'Poděkování rezervační formulář',
            };
            this.gtmService.pushGtm(dataLayer);
          },
          error: (error) => {
            throwError(() => 'Something went wrong:' + error);
          },
        });
    } else if (this.data?.type === 'carInfo') {
      const callBackFormData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: message,
        ip: this.userIp,
        marketingAgree: formData.marketingAgree,
      };
      this.carInfoService
        .sendForm(this.data!.car.aaaid, callBackFormData)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.form.reset();
            this.form.markAsUntouched();
            this.step = 2;

            const dataLayer = {
              event: 'page_view',
              pageURL: '/' + this.globals.language() + '/podekovani-popavka-cooming-soon',
              pageType: 'Purchase-10',
              pageTitle: 'Poděkování zájem na vůz',
            };
            this.gtmService.pushGtm(dataLayer);
          },
          error: (error) => {
            throwError(() => 'Something went wrong:' + error);
          },
        });
    }
  }

  ngOnInit(): void {
    this.timeOptions = this.globalsService.createTimeOptions();

    if (this.data?.type === 'reservation') {
      const dataLayer = {
        event: 'page_view',
        pageURL: '/' + this.globals.language() + '/formular-rezervace-vozu',
        pageType: 'Form-2',
        pageTitle: 'Rezervační formulář',
      };
      this.gtmService.pushGtm(dataLayer);
    } else if (this.data?.type === 'carInfo') {
      const dataLayer = {
        event: 'page_view',
        pageURL: '/' + this.globals.language() + '/formular-poptavka-cooming-soon',
        pageType: 'Form-10',
        pageTitle: 'Formulář zájem na vůz',
      };
      this.gtmService.pushGtm(dataLayer);
    }

    this.carInfoService
      .getUserIp()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.userIp = response.ip;
        },
        error: () => {
          this.userIp = '108.121.121.211'; // Error set placeholder
        },
      });

    if (this.data?.type === 'carInfo') {
      this.form.get('date')?.clearValidators();
      this.form.get('time')?.clearValidators();
    } else {
      this.form.get('date')?.setValidators([Validators.required]);
      this.form.get('time')?.setValidators([Validators.required]);
    }
    this.form.get('date')?.updateValueAndValidity();
    this.form.get('time')?.updateValueAndValidity();

    this.form
      .get('sellCar')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: boolean) => {
        if (value) {
          this.form.get('sellCarMakeModel')?.setValidators([Validators.required]);
          this.form.get('sellCarMileage')?.setValidators([Validators.required]);
          this.form.get('sellCarYear')?.setValidators([Validators.required]);
          this.form.get('sellCarFuel')?.setValidators([Validators.required]);
          this.form.get('sellCarPrice')?.setValidators([Validators.required]);
        } else {
          this.form.get('sellCarMakeModel')?.clearValidators();
          this.form.get('sellCarMileage')?.clearValidators();
          this.form.get('sellCarYear')?.clearValidators();
          this.form.get('sellCarFuel')?.clearValidators();
          this.form.get('sellCarPrice')?.clearValidators();
          this.form.patchValue({
            sellCarMakeModel: undefined,
            sellCarMileage: '',
            sellCarYear: '',
            sellCarFuel: '',
            sellCarPrice: '',
          });
        }
        this.form.get('sellCarMakeModel')?.updateValueAndValidity();
        this.form.get('sellCarMileage')?.updateValueAndValidity();
        this.form.get('sellCarYear')?.updateValueAndValidity();
        this.form.get('sellCarFuel')?.updateValueAndValidity();
        this.form.get('sellCarPrice')?.updateValueAndValidity();
      });

    this.updateMergedMileageOptions();
  }
}
